/**
 * Section variant registry (NOS-152).
 *
 * Variant ids are stored in section content as `content.variant` (JSONB — no
 * migration). A missing or unknown id always renders the block's default
 * layout, so old data and forward-compat are safe.
 *
 * Thumbs are schematic wireframes (plain divs), not scaled live renders —
 * legible at 96px wide and zero maintenance when block markup changes.
 */
import type { FC } from "react"
import type { BlockType } from "@/components/website-builder/blocks/types"

export interface BlockVariantDef {
  id: string
  label: string
  /** Schematic wireframe drawn with divs; rendered at ~96×60 in the picker. */
  Thumb: FC
}

/* Wireframe primitives — muted blocks on a card background */
const bar = (w: string, h = 4): React.CSSProperties => ({ width: w, height: h, borderRadius: 2, background: "#8b8b96" })
const accent = (w: string, h = 6): React.CSSProperties => ({ width: w, height: h, borderRadius: 2, background: "#6d9eeb" })
const box = (extra: React.CSSProperties = {}): React.CSSProperties => ({ background: "#3a3a44", borderRadius: 3, ...extra })
const frame: React.CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: 8,
  boxSizing: "border-box",
}

const HeroCenteredThumb: FC = () => (
  <div style={{ ...frame, alignItems: "center", justifyContent: "center", background: "#4a5b8a", borderRadius: 4 }}>
    <div style={bar("55%", 6)} />
    <div style={bar("70%", 3)} />
    <div style={accent("28%", 7)} />
  </div>
)

const HeroSplitThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", alignItems: "center", gap: 6 }}>
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={bar("90%", 6)} />
      <div style={bar("70%", 3)} />
      <div style={accent("45%", 7)} />
    </div>
    <div style={box({ flex: 1, height: "80%" })} />
  </div>
)

const HeroFullBleedThumb: FC = () => (
  <div style={{ ...frame, justifyContent: "flex-end", background: "linear-gradient(to top, #2d2d36, #4a5b8a)", borderRadius: 4 }}>
    <div style={bar("50%", 6)} />
    <div style={bar("65%", 3)} />
    <div style={accent("26%", 7)} />
  </div>
)

const HeroMinimalThumb: FC = () => (
  <div style={{ ...frame, alignItems: "center", justifyContent: "center" }}>
    <div style={bar("60%", 7)} />
    <div style={bar("42%", 3)} />
    <div style={{ ...accent("20%", 2), marginTop: 3 }} />
  </div>
)

const StatsCardsThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", alignItems: "center", gap: 5 }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={box({ flex: 1, height: "70%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 })}>
        <div style={accent("50%", 6)} />
        <div style={bar("65%", 2)} />
      </div>
    ))}
  </div>
)

const StatsInlineBandThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", alignItems: "center", justifyContent: "space-around", background: "#4a5b8a", borderRadius: 4 }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
        <div style={{ ...bar("100%", 7), width: 16, background: "#e8e8ee" }} />
        <div style={{ ...bar("100%", 2), width: 22 }} />
      </div>
    ))}
  </div>
)

const StatsOversizedThumb: FC = () => (
  <div style={{ ...frame, justifyContent: "center", gap: 5 }}>
    {[0, 1].map((i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={accent("30%", 9)} />
        <div style={bar("40%", 3)} />
      </div>
    ))}
  </div>
)

const CardsGridThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", gap: 5 }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={box({ flex: 1, display: "flex", flexDirection: "column", gap: 3, padding: 4 })}>
        <div style={box({ height: 14, background: "#55555f" })} />
        <div style={bar("80%", 3)} />
        <div style={bar("95%", 2)} />
      </div>
    ))}
  </div>
)

const CardsFeatureListThumb: FC = () => (
  <div style={{ ...frame, justifyContent: "center", gap: 6 }}>
    {[0, 1].map((i) => (
      <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
        <div style={{ width: 10, height: 10, borderRadius: 999, background: "#6d9eeb", flexShrink: 0 }} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={bar("50%", 3)} />
          <div style={bar("85%", 2)} />
        </div>
      </div>
    ))}
  </div>
)

const CardsOverlapThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", gap: 5 }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={box({ flex: 1, background: "#4a5b8a", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 4, gap: 2 })}>
        <div style={{ ...bar("75%", 3), background: "#e8e8ee" }} />
        <div style={bar("55%", 2)} />
      </div>
    ))}
  </div>
)

const TestimonialsGridThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", gap: 5, alignItems: "center" }}>
    {[0, 1].map((i) => (
      <div key={i} style={box({ flex: 1, height: "72%", display: "flex", flexDirection: "column", gap: 3, padding: 5 })}>
        <div style={bar("90%", 2)} />
        <div style={bar("75%", 2)} />
        <div style={{ ...accent("40%", 2), marginTop: 2 }} />
      </div>
    ))}
  </div>
)

const TestimonialsSpotlightThumb: FC = () => (
  <div style={{ ...frame, alignItems: "center", justifyContent: "center", gap: 3 }}>
    <div style={{ ...accent("12%", 8), borderRadius: 4 }} />
    <div style={bar("80%", 3)} />
    <div style={bar("62%", 3)} />
    <div style={{ ...bar("30%", 2), marginTop: 2 }} />
  </div>
)

const TestimonialsWallThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", gap: 4 }}>
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={box({ height: "55%" })} />
      <div style={box({ height: "35%", background: "#4a5b8a" })} />
    </div>
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={box({ height: "35%", background: "#4a5b8a" })} />
      <div style={box({ height: "55%" })} />
    </div>
  </div>
)

const CtaBannerThumb: FC = () => (
  <div style={{ ...frame, alignItems: "center", justifyContent: "center", background: "#4a5b8a", borderRadius: 6, gap: 4 }}>
    <div style={{ ...bar("55%", 5), background: "#e8e8ee" }} />
    <div style={{ ...bar("70%", 2), background: "#c9c9d4" }} />
    <div style={{ width: "26%", height: 7, borderRadius: 2, background: "#ffffff" }} />
  </div>
)

const CtaSplitPanelThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", alignItems: "center", gap: 6 }}>
    <div style={{ flex: 1.2, display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={bar("85%", 5)} />
      <div style={bar("95%", 2)} />
      <div style={bar("70%", 2)} />
    </div>
    <div style={box({ flex: 1, height: "82%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 })}>
      <div style={accent("55%", 7)} />
      <div style={bar("65%", 2)} />
    </div>
  </div>
)

const CtaFloatingCardThumb: FC = () => (
  <div style={{ ...frame, alignItems: "center", justifyContent: "center", background: "#33333c", borderRadius: 4 }}>
    <div style={box({ width: "70%", height: "78%", background: "#e8e8ee", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 })}>
      <div style={{ ...bar("60%", 4), background: "#3a3a44" }} />
      <div style={{ ...bar("75%", 2), background: "#8b8b96" }} />
      <div style={accent("40%", 6)} />
    </div>
  </div>
)


const HeroFloatingThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", alignItems: "center", gap: 6 }}>
    <div style={{ flex: 1.1, display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={bar("90%", 6)} />
      <div style={bar("70%", 3)} />
      <div style={accent("45%", 7)} />
    </div>
    <div style={{ flex: 1, position: "relative", height: "80%" }}>
      <div style={{ position: "absolute", right: -2, top: -2, width: "70%", height: "70%", borderRadius: 999, background: "#6d9eeb", opacity: 0.35 }} />
      <div style={box({ position: "absolute", inset: 0, top: 6, left: 6, background: "#4a5b8a", boxShadow: "0 3px 6px rgb(0 0 0 / 0.4)" })} />
    </div>
  </div>
)

const HeroVideoThumb: FC = () => (
  <div style={{ ...frame, alignItems: "center", justifyContent: "center", background: "#33333c", borderRadius: 4, position: "relative" }}>
    <div style={{ width: 0, height: 0, borderLeft: "10px solid #e8e8ee", borderTop: "6px solid transparent", borderBottom: "6px solid transparent", marginBottom: 4 }} />
    <div style={{ ...bar("55%", 4), background: "#e8e8ee", position: "absolute", bottom: 10 }} />
  </div>
)

const StatsCountUpThumb: FC = () => (
  <div style={{ ...frame, flexDirection: "row", alignItems: "center", justifyContent: "space-around" }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
        <div style={accent("100%", 8)} />
        <div style={bar("80%", 2)} />
      </div>
    ))}
  </div>
)

export const BLOCK_VARIANTS: Partial<Record<BlockType, BlockVariantDef[]>> = {
  hero: [
    { id: "centered", label: "Centered", Thumb: HeroCenteredThumb },
    { id: "split", label: "Split", Thumb: HeroSplitThumb },
    { id: "full-bleed", label: "Full bleed", Thumb: HeroFullBleedThumb },
    { id: "minimal", label: "Minimal", Thumb: HeroMinimalThumb },
    { id: "video", label: "Video", Thumb: HeroVideoThumb },
    { id: "floating", label: "Floating image", Thumb: HeroFloatingThumb },
  ],
  impact_stats: [
    { id: "cards", label: "Cards", Thumb: StatsCardsThumb },
    { id: "inline-band", label: "Band", Thumb: StatsInlineBandThumb },
    { id: "oversized", label: "Oversized", Thumb: StatsOversizedThumb },
    { id: "count-up", label: "Count-up", Thumb: StatsCountUpThumb },
  ],
  cards: [
    { id: "grid", label: "Grid", Thumb: CardsGridThumb },
    { id: "feature-list", label: "Feature list", Thumb: CardsFeatureListThumb },
    { id: "overlap", label: "Overlay", Thumb: CardsOverlapThumb },
  ],
  testimonials: [
    { id: "grid", label: "Grid", Thumb: TestimonialsGridThumb },
    { id: "spotlight", label: "Spotlight", Thumb: TestimonialsSpotlightThumb },
    { id: "wall", label: "Wall", Thumb: TestimonialsWallThumb },
  ],
  donation_cta: [
    { id: "banner", label: "Banner", Thumb: CtaBannerThumb },
    { id: "split-panel", label: "Split panel", Thumb: CtaSplitPanelThumb },
    { id: "floating-card", label: "Floating card", Thumb: CtaFloatingCardThumb },
  ],
}

/** Read the stored variant id; undefined means "default". */
export function getSectionVariant(content: Record<string, unknown> | null): string | undefined {
  const v = content?.variant
  return typeof v === "string" && v.length > 0 ? v : undefined
}
